import type {
  AuthenticatedUserContract,
  CaptureReadinessContract,
  FinancialQueueItemContract,
  FinancialSyncOutcomeContract,
  Locale,
  MessageKey,
  ReplicaChangePageContract,
  ReplicaSnapshotPageContract,
  StudentReplicaContract,
} from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { INSTALLATION_REVOKED_EVENT, deviceFetch, json, localStore } from "./client.js";
import {
  captureBlockReason,
  requestBrowserStorageReadiness,
  type CaptureBlockReason,
} from "./storage-readiness.js";

/** Capture warns after 72 hours without a synchronization run. */
export const READINESS_WARNING_MS = 72 * 60 * 60 * 1_000;
/** Capture is blocked only after seven days without a synchronization run. */
export const READINESS_BLOCK_MS = 7 * 24 * 60 * 60 * 1_000;
const READINESS_CALM_MS = 24 * 60 * 60 * 1_000;
const ONLINE_REFRESH_MS = 60_000;

export type ReadinessTier = "ok" | "warning" | "blocked";
export type SyncDotTier = "green" | "amber" | "red";

export type WorkspaceBannerKind =
  "capture-blocked" | "offline" | "sync-failed" | "update-after-sync" | "update-ready" | null;

export function updateBannerSupersedesServerSessionNotice(banner: WorkspaceBannerKind): boolean {
  return banner === "update-ready" || banner === "update-after-sync";
}

interface SyncStorageReadiness {
  readonly persistenceGranted: boolean;
  readonly storageHeadroom: boolean;
  readonly authorized: true;
}

async function pullChangePages(readiness: SyncStorageReadiness): Promise<StudentReplicaContract> {
  let replica: StudentReplicaContract | null = null;
  let hasMore = true;
  while (hasMore) {
    const cursor = await localStore.syncCursor();
    const page = await json<ReplicaChangePageContract>(
      await deviceFetch(`/api/sync/changes?after=${encodeURIComponent(cursor)}&limit=50`),
    );
    if (page.hasMore && page.cursor === cursor)
      throw new Error("Synchronization cursor did not advance");
    replica = await localStore.applyChangePage(page, readiness);
    hasMore = page.hasMore;
  }
  if (!replica) throw new Error("Synchronization returned no change page");
  return replica;
}

async function pullFullSnapshot(readiness: SyncStorageReadiness): Promise<StudentReplicaContract> {
  let pageNumber = 1;
  let replica: StudentReplicaContract | null = null;
  let hasMore = true;
  while (hasMore) {
    const page = await json<ReplicaSnapshotPageContract>(
      await deviceFetch(`/api/sync/snapshot?page=${pageNumber}&pageSize=50`),
    );
    replica = await localStore.applySnapshotPage(page, readiness);
    hasMore = page.hasMore;
    pageNumber += 1;
  }
  if (!replica) throw new Error("Synchronization returned no snapshot page");
  return replica;
}

/** At most one banner is ever shown; this is the single arbitration point. */
export function workspaceBanner({
  captureBlocked,
  online,
  syncFailed,
  updateReady,
  updateBlocked,
}: {
  readonly captureBlocked: boolean;
  readonly online: boolean;
  readonly syncFailed: boolean;
  readonly updateReady: boolean;
  readonly updateBlocked: boolean;
}): WorkspaceBannerKind {
  if (online && syncFailed) return "sync-failed";
  if (captureBlocked) return "capture-blocked";
  if (!online) return "offline";
  if (updateBlocked) return "update-after-sync";
  if (updateReady) return "update-ready";
  return null;
}

/** Relative sync age, western digits in both languages. */
export function relativeAgeLabel(milliseconds: number, locale: Locale): string {
  const format = new Intl.RelativeTimeFormat(
    locale === "ar" ? "ar-MA-u-nu-latn" : "fr-MA-u-nu-latn",
    { numeric: "auto" },
  );
  const minutes = Math.max(0, Math.round(milliseconds / 60_000));
  if (minutes < 60) return format.format(-minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (hours < 48) return format.format(-hours, "hour");
  return format.format(-Math.round(hours / 24), "day");
}

export interface WorkspaceController {
  readonly replica: StudentReplicaContract;
  readonly queue: readonly FinancialQueueItemContract[];
  readonly pendingCount: number;
  readonly readiness: CaptureReadinessContract | null;
  readonly readinessKey: MessageKey;
  readonly syncing: boolean;
  readonly syncFailed: boolean;
  readonly statusLabel: string;
  readonly canSync: boolean;
  /** Milliseconds since the last successful synchronization. */
  readonly syncAgeMilliseconds: number;
  /** Warning tier at 72 h, blocked tier at seven days. */
  readonly readinessTier: ReadinessTier;
  /** Chrome dot: green ≤ 24 h, amber up to seven days, red beyond. */
  readonly syncDot: SyncDotTier;
  /** Elapsed share of the seven-day block threshold, clamped to 0…1. */
  readonly readinessProgress: number;
  readonly captureBlocked: boolean;
  readonly captureBlockReason: CaptureBlockReason;
  readonly sync: () => Promise<boolean>;
  readonly refreshLocalStatus: () => Promise<void>;
}

/**
 * Owns the encrypted replica, the payment queue, capture readiness and the
 * synchronization run. Both presentations read this and neither may re-implement it.
 */
export function useWorkspace({
  locale,
  user,
  csrf,
  online,
  initialReplica,
}: {
  readonly locale: Locale;
  readonly user: AuthenticatedUserContract | null;
  readonly csrf: string | null;
  readonly online: boolean;
  readonly initialReplica: StudentReplicaContract;
}): WorkspaceController {
  const [replica, setReplica] = useState(initialReplica);
  const [queue, setQueue] = useState<readonly FinancialQueueItemContract[]>([]);
  const [readiness, setReadiness] = useState<CaptureReadinessContract | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);
  const syncInFlight = useRef<Promise<boolean> | null>(null);

  const refreshLocalStatus = useCallback(async () => {
    const development = import.meta.env.DEV
      ? await import("../DevelopmentTools.js").then((module) => module.readDevelopmentSyncOptions())
      : { clockHours: 0 };
    const offset = development.clockHours * 60 * 60_000;
    setQueue(await localStore.queueItems());
    setReadiness(
      await localStore.captureReadiness(Date.now() + offset, performance.now() + offset),
    );
  }, []);

  useEffect(() => {
    void refreshLocalStatus();
  }, [refreshLocalStatus]);

  const sync = useCallback((): Promise<boolean> => {
    if (!online || !user || !csrf) return Promise.resolve(false);
    if (syncInFlight.current) return syncInFlight.current;

    const operation = (async () => {
      setSyncing(true);
      setSyncFailed(false);
      let succeeded = false;
      try {
        const development = import.meta.env.DEV
          ? await import("../DevelopmentTools.js").then((module) =>
              module.readDevelopmentSyncOptions(),
            )
          : { delivery: "normal" as const, storageReady: false };
        const storageReadiness = await requestBrowserStorageReadiness(development.storageReady);
        let developmentOutcomeApplied = false;
        const synchronizedQueue = await localStore.synchronizeFinancialQueue(async (submission) => {
          const delivery = developmentOutcomeApplied ? "normal" : development.delivery;
          developmentOutcomeApplied = true;
          if (delivery === "transport") throw new Error("Development transport interruption");
          const url = `/api/sync/payments/${submission.record.payment.id}`;
          const response = await deviceFetch(url, {
            method: "PUT",
            headers: {
              "content-type": "application/json",
              ...(delivery === "rejected"
                ? { "x-chams-local-test-outcome": "reject-over-allocation" }
                : delivery === "revoked"
                  ? { "x-chams-local-test-outcome": "quarantine-revoked" }
                  : {}),
            },
            body: JSON.stringify(submission),
          });
          if (delivery === "lost-response") throw new Error("Development committed response loss");
          if (response.status !== 200 && response.status !== 409)
            throw new Error(`Synchronization failed: ${response.status}`);
          return (await response.json()) as FinancialSyncOutcomeContract;
        });
        if (synchronizedQueue.some((item) => item.reasonCode === "INSTALLATION_REVOKED")) {
          window.dispatchEvent(new Event(INSTALLATION_REVOKED_EVENT));
          throw new Error("Installation was revoked during synchronization");
        }
        const readiness = { ...storageReadiness, authorized: true } satisfies SyncStorageReadiness;
        const captureReadiness = await localStore.captureReadiness();
        const cleanQueue = (await localStore.unsynchronizedCount()) === 0;
        const leaseRecovery =
          captureReadiness.code === "LEASE_EXPIRED" || captureReadiness.code === "CLOCK_UNCERTAIN";
        let synchronizedReplica: StudentReplicaContract;
        if (leaseRecovery && cleanQueue) {
          synchronizedReplica = await pullFullSnapshot(readiness);
        } else {
          try {
            synchronizedReplica = await pullChangePages(readiness);
          } catch (error) {
            if (!cleanQueue) throw error;
            synchronizedReplica = await pullFullSnapshot(readiness);
          }
        }
        setReplica(synchronizedReplica);
        succeeded = true;
      } catch {
        setSyncFailed(true);
      } finally {
        try {
          await refreshLocalStatus();
        } finally {
          syncInFlight.current = null;
          setSyncing(false);
        }
      }
      return succeeded;
    })();
    syncInFlight.current = operation;
    return operation;
  }, [csrf, online, refreshLocalStatus, user]);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const synchronizedAt = Date.parse(replica.synchronizedAt);
  const syncAgeMilliseconds = Number.isFinite(synchronizedAt)
    ? Math.max(0, now - synchronizedAt)
    : 0;
  const blockReason = captureBlockReason(readiness, syncAgeMilliseconds, READINESS_BLOCK_MS);
  const captureBlocked = blockReason !== null;
  const readinessTier: ReadinessTier = captureBlocked
    ? "blocked"
    : syncAgeMilliseconds >= READINESS_WARNING_MS
      ? "warning"
      : "ok";
  const syncDot: SyncDotTier = captureBlocked
    ? "red"
    : syncAgeMilliseconds >= READINESS_CALM_MS
      ? "amber"
      : "green";
  const readinessProgress = Math.min(1, syncAgeMilliseconds / READINESS_BLOCK_MS);

  const pendingCount = queue.filter((item) => item.state !== "synced").length;
  const pendingSignature = queue
    .filter((item) => item.state !== "synced")
    .map((item) => `${item.paymentId}:${item.state}`)
    .join("|");

  useEffect(() => {
    if (online && user && csrf) void sync();
  }, [csrf, online, pendingSignature, sync, user]);

  useEffect(() => {
    if (!online || !user || !csrf) return;
    const synchronizeVisibleWorkspace = () => {
      if (document.visibilityState === "visible") void sync();
    };
    const timer = window.setInterval(synchronizeVisibleWorkspace, ONLINE_REFRESH_MS);
    window.addEventListener("focus", synchronizeVisibleWorkspace);
    document.addEventListener("visibilitychange", synchronizeVisibleWorkspace);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", synchronizeVisibleWorkspace);
      document.removeEventListener("visibilitychange", synchronizeVisibleWorkspace);
    };
  }, [csrf, online, sync, user]);

  const readinessKey: MessageKey =
    readiness?.code === "LEASE_WARNING_24H"
      ? "sync.warning24"
      : readiness?.code === "LEASE_WARNING_72H"
        ? "sync.warning72"
        : readiness?.allowed === false
          ? "sync.blocked"
          : "sync.ready";
  const statusLabel = syncFailed
    ? translate(locale, "sync.failed")
    : syncing
      ? translate(locale, "profile.syncing")
      : online
        ? pendingCount === 0
          ? translate(locale, "profile.synchronized")
          : `${pendingCount} ${translate(locale, "profile.pendingCount")}`
        : translate(locale, "profile.offline");

  return {
    replica,
    queue,
    pendingCount,
    readiness,
    readinessKey,
    syncing,
    syncFailed,
    statusLabel,
    canSync: online && Boolean(user) && !syncing,
    syncAgeMilliseconds,
    readinessTier,
    syncDot,
    readinessProgress,
    captureBlocked,
    captureBlockReason: blockReason,
    sync,
    refreshLocalStatus,
  };
}
